import { useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toPng } from "html-to-image";
import { Check, ChevronRight, Copy, Crown, Download, Loader2, Share2, Swords, TrendingDown, TrendingUp, Users, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@v2/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";
import { ELEVENBASE_LOGO_URL } from "../../components/brand";
import { RadarChart, type RadarDataset } from "../../components/charts";
import { HowItCountsDialog } from "./HowItCountsDialog";
import { radarValuesFor, COLORS } from "../players/comparison-math";
import {
  EA_POSITIONS,
  aggregateScore,
  computeBenchmark,
  refinedKpi,
  reliabilityOf,
  statValueOf,
  type Benchmark,
  type EaPosition,
  type PositionStats,
} from "../players/comparison-math";

// Soglia minima nel ruolo per essere mostrati nel pannello. Vale per il
// player stesso (filtro dei ruoli da mostrare) e per i compagni (filtro dei
// "rispetto a chi mi confronto"). Sotto questa soglia il dato è troppo
// volatile per un confronto significativo.
const MIN_MATCHES = 3;

type PositionsPlayedRow = {
  player_id: string;
  display_name: string;
  position: string;
  matches: number;
};

// Mapping dei 6 valori di radarValuesFor sulle relative etichette i18n.
// Sincronizzato con radarValuesFor() in comparison-math.ts.
const ROLE_METRIC_LABELS: Record<EaPosition, [string, string, string, string, string, string]> = {
  goalkeeper: [
    "compare.metric.avgRating",
    "compare.metric.winRate",
    "compare.metric.cleanSheetsPerMatch",
    "compare.metric.savesPerMatch",
    "compare.metric.momPct",
    "compare.metric.minutesAvg",
  ],
  defender: [
    "compare.metric.avgRating",
    "compare.metric.winRate",
    "compare.metric.tacklePct",
    "compare.metric.passPct",
    "compare.metric.momPct",
    "compare.metric.assistsPerMatch",
  ],
  midfielder: [
    "compare.metric.avgRating",
    "compare.metric.winRate",
    "compare.metric.goalsPerMatch",
    "compare.metric.assistsPerMatch",
    "compare.metric.momPct",
    "compare.metric.passPct",
  ],
  forward: [
    "compare.metric.avgRating",
    "compare.metric.winRate",
    "compare.metric.goalsPerMatch",
    "compare.metric.assistsPerMatch",
    "compare.metric.momPct",
    "compare.metric.passPct",
  ],
};

// Formatter per i valori "raw" (non normalizzati 0-100) usati nelle pillole
// KPI sopra ogni ruolo. Mantieni allineato con radarValuesFor per significato.
type MetricKey =
  | "avg_rating"
  | "win_rate"
  | "goals_per_match"
  | "assists_per_match"
  | "mom_per_match"
  | "pass_pct"
  | "tackle_pct"
  | "clean_sheets_per_match"
  | "saves_per_match"
  | "minutes_avg";

function formatRawValue(key: MetricKey, v: number): string {
  switch (key) {
    case "avg_rating":
      return v.toFixed(2);
    case "win_rate":
    case "pass_pct":
    case "tackle_pct":
    case "mom_per_match":
      return `${v.toFixed(1)}%`;
    case "goals_per_match":
    case "assists_per_match":
    case "clean_sheets_per_match":
    case "saves_per_match":
      return v.toFixed(2);
    case "minutes_avg":
      return `${v.toFixed(0)}'`;
  }
}

// Chiavi raw del PositionStats da estrarre per ogni slot del radar, oppure
// "computed" se la stat va calcolata (es. clean_sheets / matches). Sincronizzato
// con radarValuesFor.
type SlotSource =
  | { kind: "stat"; key: MetricKey; benchmarkKey?: keyof Benchmark }
  | { kind: "perMatch"; numerator: "clean_sheets" | "saves"; benchmarkKey?: keyof Benchmark };

function slotSources(position: EaPosition): SlotSource[] {
  if (position === "goalkeeper") {
    return [
      { kind: "stat", key: "avg_rating",        benchmarkKey: "avg_rating" },
      { kind: "stat", key: "win_rate",          benchmarkKey: "win_rate" },
      { kind: "perMatch", numerator: "clean_sheets" },
      { kind: "perMatch", numerator: "saves" },
      { kind: "stat", key: "mom_per_match",     benchmarkKey: "mom_per_match" },
      { kind: "stat", key: "minutes_avg" },
    ];
  }
  if (position === "defender") {
    return [
      { kind: "stat", key: "avg_rating",        benchmarkKey: "avg_rating" },
      { kind: "stat", key: "win_rate",          benchmarkKey: "win_rate" },
      { kind: "stat", key: "tackle_pct",        benchmarkKey: "tackle_pct" },
      { kind: "stat", key: "pass_pct",          benchmarkKey: "pass_pct" },
      { kind: "stat", key: "mom_per_match",     benchmarkKey: "mom_per_match" },
      { kind: "stat", key: "assists_per_match", benchmarkKey: "assists_per_match" },
    ];
  }
  // mid / fwd
  return [
    { kind: "stat", key: "avg_rating",        benchmarkKey: "avg_rating" },
    { kind: "stat", key: "win_rate",          benchmarkKey: "win_rate" },
    { kind: "stat", key: "goals_per_match",   benchmarkKey: "goals_per_match" },
    { kind: "stat", key: "assists_per_match", benchmarkKey: "assists_per_match" },
    { kind: "stat", key: "mom_per_match",     benchmarkKey: "mom_per_match" },
    { kind: "stat", key: "pass_pct",          benchmarkKey: "pass_pct" },
  ];
}

function rawValueOf(stat: PositionStats, source: SlotSource, benchmark: Benchmark | null): number {
  if (source.kind === "stat") {
    if (source.benchmarkKey) {
      return refinedKpi(stat, source.benchmarkKey, benchmark);
    }
    // Metriche fuori dal benchmark (es. minutes_avg per il portiere): dato raw
    return Number((stat[source.key as keyof PositionStats] as number | null | undefined) ?? 0);
  }
  // perMatch: clean_sheets / matches o saves / matches
  const num = Number(stat[source.numerator] ?? 0);
  return stat.matches > 0 ? num / stat.matches : 0;
}

function metricKeyFromSource(source: SlotSource): MetricKey {
  if (source.kind === "stat") return source.key;
  if (source.numerator === "clean_sheets") return "clean_sheets_per_match";
  return "saves_per_match";
}

// Calcola il benchmark "per partita" per le metriche derivate (cs/m, saves/m)
// che non sono nel Benchmark standard. Restituisce un dizionario opzionale
// usato per i delta vs squadra del portiere.
function computePerMatchBenchmark(rows: PositionStats[]): { clean_sheets_per_match: number; saves_per_match: number } | null {
  if (rows.length === 0) return null;
  let cs = 0, sv = 0, n = 0;
  for (const r of rows) {
    if (r.matches > 0) {
      cs += r.clean_sheets / r.matches;
      sv += r.saves / r.matches;
      n += 1;
    }
  }
  if (n === 0) return null;
  return { clean_sheets_per_match: cs / n, saves_per_match: sv / n };
}

export function PlayerRoleComparison({
  teamId,
  playerId,
  onOpenPlayer,
}: {
  teamId: string;
  playerId: string;
  onOpenPlayer?: (id: string) => void;
}) {
  const { t } = useTranslation("players");

  // 1) Ottiene tutti i player con tutte le posizioni ricoperte (1 RPC).
  const positionsPlayedQuery = useQuery({
    queryKey: ["role-comparison-positions", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_player_positions_played", {
        p_team_id: teamId,
        p_player_ids: null,
      });
      if (error) throw error;
      return (data || []) as PositionsPlayedRow[];
    },
    staleTime: 60_000,
  });

  // 2) I ruoli del player corrente con >= MIN_MATCHES match in posizione.
  const myRoles = useMemo<EaPosition[]>(() => {
    const rows = positionsPlayedQuery.data || [];
    const set = new Set<EaPosition>();
    for (const r of rows) {
      if (r.player_id !== playerId) continue;
      if (!EA_POSITIONS.includes(r.position as EaPosition)) continue;
      if (r.matches >= MIN_MATCHES) set.add(r.position as EaPosition);
    }
    // Ordine fisso: GK, DEF, MID, FWD
    return EA_POSITIONS.filter((p) => set.has(p));
  }, [positionsPlayedQuery.data, playerId]);

  // 3) Per ogni ruolo del player: lista di tutti i player con >= 1 match in
  //    posizione (incluso il player stesso). Da qui calcolo benchmark e
  //    estraggo player corrente + compagni eleggibili (>= MIN_MATCHES).
  const roleStatQueries = useQueries({
    queries: myRoles.map((pos) => {
      // tutti gli ID che hanno almeno 1 match in pos (per la RPC)
      const ids = Array.from(
        new Set(
          (positionsPlayedQuery.data || [])
            .filter((r) => r.position === pos && r.matches > 0)
            .map((r) => r.player_id),
        ),
      );
      return {
        queryKey: ["role-comparison-stats", teamId, pos, ids.join(",")],
        enabled: ids.length > 0,
        queryFn: async () => {
          const { data, error } = await supabaseV2.rpc("team_player_position_stats", {
            p_team_id: teamId,
            p_player_ids: ids,
            p_position: pos,
            p_period_days: null,
          });
          if (error) throw error;
          return data as { players: PositionStats[] };
        },
        staleTime: 30_000,
      };
    }),
  });

  if (positionsPlayedQuery.isLoading) {
    return <SectionLoading />;
  }

  if (myRoles.length === 0) {
    return (
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {t("roleComparison.title")}
          </CardTitle>
          <CardDescription>{t("roleComparison.desc", { min: MIN_MATCHES })}</CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <EmptyState text={t("roleComparison.noRolesYet", { min: MIN_MATCHES })} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              {t("roleComparison.title")}
            </CardTitle>
            <CardDescription>{t("roleComparison.desc", { min: MIN_MATCHES })}</CardDescription>
          </div>
          <HowItCountsDialog variant="full" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {myRoles.map((pos, idx) => {
          const q = roleStatQueries[idx];
          return (
            <RoleSection
              key={pos}
              position={pos}
              statsRows={q.data?.players || []}
              isLoading={q.isLoading}
              playerId={playerId}
              t={t}
              onOpenPlayer={onOpenPlayer}
            />
          );
        })}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Una sezione "ruolo" con: KPI del player vs media squadra, lista compagni
// con score 0-100 e match nel ruolo.
// ──────────────────────────────────────────────────────────────────────────

function RoleSection({
  position,
  statsRows,
  isLoading,
  playerId,
  t,
  onOpenPlayer,
}: {
  position: EaPosition;
  statsRows: PositionStats[];
  isLoading: boolean;
  playerId: string;
  t: (k: string, opts?: any) => string;
  onOpenPlayer?: (id: string) => void;
}) {
  // Ref alla "preview da condividere" (off-screen). html-to-image richiede
  // un DOM realmente renderizzato e con dimensioni > 0, quindi la teniamo
  // montata ma fuori dalla viewport visibile (vedi style nel JSX).
  const shareRef = useRef<HTMLDivElement | null>(null);
  const shareVsRef = useRef<HTMLDivElement | null>(null);
  // Stato del dialog di condivisione: kind = "single" | "versus" o null
  // (chiuso). Quando aperto la prima volta generiamo il PNG dall'off-screen
  // e lo mostriamo visibile nel dialog (l'utente può scaricarlo, copiarlo
  // negli appunti o aprirne lo share sheet nativo).
  const [shareDialog, setShareDialog] = useState<ShareDialogState | null>(null);
  // Confronto 1vs1: id del compagno selezionato (toggle dal tap sulla row).
  // Null = nessun confronto attivo, mostro solo la classifica generale.
  const [versusId, setVersusId] = useState<string | null>(null);

  // Hook di generazione PNG: DEVE stare prima di qualunque early-return
  // (loading / no-data), altrimenti React rileva un numero variabile di hook
  // tra render successivi (errore #310).
  useEffect(() => {
    if (!shareDialog || shareDialog.dataUrl || shareDialog.error) return;
    let cancelled = false;
    const target = shareDialog.kind === "single" ? shareRef.current : shareVsRef.current;
    if (!target) {
      setShareDialog((cur) =>
        cur && cur.kind === shareDialog.kind ? { ...cur, error: "missing_preview" } : cur,
      );
      return;
    }
    (async () => {
      try {
        await waitForImages(target);
        // Aspetto 2 frame di animation: garantisce che il browser abbia
        // completato il layout del container fixed (top: -10000px) prima di
        // chiamare toPng. Senza questo step, in alcuni casi rect.height era 0
        // e l'output veniva tutto nero.
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
        const rect = target.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        const dataUrl = await toPng(target, {
          width,
          height,
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: "#0a1114",
          // CSP del sito blocca il fetch da fonts.googleapis.com. Senza
          // skipFonts html-to-image fallisce l'embed CSS e produce un PNG
          // nero/vuoto. Il preview usa già un font-family system-only, quindi
          // saltare l'embed font non cambia il risultato visivo.
          skipFonts: true,
          fontEmbedCSS: "",
          // Forzo le dimensioni del clone interno: ho visto casi in cui il
          // target ha rect.height = 0 perché il position:fixed off-screen
          // non passa il flag "rendered" al layout engine.
          style: {
            width: `${width}px`,
            height: `${height}px`,
            position: "static",
            top: "auto",
            left: "auto",
          },
        });
        if (cancelled) return;
        setShareDialog((cur) =>
          cur && cur.kind === shareDialog.kind ? { ...cur, dataUrl } : cur,
        );
      } catch (err) {
        if (cancelled) return;
        console.error("[PlayerRoleComparison] toPng failed", err);
        setShareDialog((cur) =>
          cur && cur.kind === shareDialog.kind
            ? { ...cur, error: err instanceof Error ? err.message : "unknown" }
            : cur,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareDialog]);

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card/60 p-4">
        <SectionLoading />
      </div>
    );
  }

  const me = statsRows.find((r) => r.player_id === playerId);
  if (!me || me.matches < MIN_MATCHES) {
    return null;
  }

  const benchmark = computeBenchmark(statsRows);
  const perMatchBenchmark = position === "goalkeeper" ? computePerMatchBenchmark(statsRows) : null;
  const myScore = aggregateScore(me, position, benchmark);
  const myReliability = reliabilityOf(me.matches);
  const myReliabilityTone =
    myReliability === "high"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : myReliability === "medium"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
        : "bg-rose-500/15 text-rose-700 dark:text-rose-400";

  // Compagni con >= MIN_MATCHES nel ruolo, esclusi me, ordinati per score.
  const teammates = statsRows
    .filter((r) => r.player_id !== playerId && r.matches >= MIN_MATCHES)
    .map((stat) => ({
      stat,
      score: aggregateScore(stat, position, benchmark),
    }))
    .sort((a, b) => b.score - a.score);

  const sources = slotSources(position);
  const labels = ROLE_METRIC_LABELS[position];
  const versus = versusId ? teammates.find((tm) => tm.stat.player_id === versusId) ?? null : null;

  const positionLabel = t(`compare.position.${position}`);
  const playerSlug = me.display_name.toLowerCase().replace(/\s+/g, "-");

  // Apre il dialog single con un placeholder. Il useEffect lato dialog
  // genera il PNG dal preview off-screen e aggiorna lo state.
  const openShareSingle = () => {
    setShareDialog({
      kind: "single",
      dataUrl: null,
      error: null,
      fileName: `elevenbase-${playerSlug}-${position}.png`,
      shareTitle: t("roleComparison.shareTitle"),
      shareText: t("roleComparison.shareText", {
        name: me.display_name,
        position: positionLabel,
        score: myScore,
      }),
    });
  };

  const openShareVs = () => {
    if (!versus) return;
    const slug = `${me.display_name}-vs-${versus.stat.display_name}`
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setShareDialog({
      kind: "versus",
      dataUrl: null,
      error: null,
      fileName: `elevenbase-${slug}-${position}.png`,
      shareTitle: t("roleComparison.versus.shareTitle"),
      shareText: t("roleComparison.versus.shareText", {
        name: me.display_name,
        other: versus.stat.display_name,
        position: positionLabel,
        myScore,
        otherScore: versus.score,
      }),
    });
  };

  return (
    <div className="rounded-xl border bg-card/60 p-3 sm:p-4 space-y-3">
      {/* Header ruolo */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="font-semibold uppercase tracking-wider">
          {t(`compare.position.${position}`)}
        </Badge>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {t("roleComparison.matchesInRole", { count: me.matches })}
        </span>
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            myReliabilityTone,
          )}
          title={t(`compare.sample.${myReliability}Tip`)}
        >
          {t(`compare.sample.${myReliability}`)}
        </span>
        <div className="ml-auto text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-none">
            {t("roleComparison.myScore")}
          </p>
          <p className="text-xl font-bold tabular-nums text-primary leading-tight">
            {myScore}
            <span className="text-xs text-muted-foreground font-medium">/100</span>
          </p>
        </div>
      </div>

      {/* KPI del player vs media squadra */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
          {t("roleComparison.vsTeam")}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {sources.map((src, i) => {
            const my = rawValueOf(me, src, benchmark);
            const teamVal = src.kind === "stat" && src.benchmarkKey
              ? benchmark?.[src.benchmarkKey] ?? null
              : src.kind === "perMatch"
                ? perMatchBenchmark?.[src.numerator === "clean_sheets" ? "clean_sheets_per_match" : "saves_per_match"] ?? null
                : null;
            return (
              <KpiTile
                key={i}
                label={t(labels[i])}
                value={formatRawValue(metricKeyFromSource(src), my)}
                teamValue={teamVal != null ? formatRawValue(metricKeyFromSource(src), teamVal) : null}
                delta={teamVal != null ? deltaPct(my, teamVal) : null}
                t={t}
              />
            );
          })}
        </div>
      </div>

      {/* Classifica compagni: ranking statico (no più tap-toggle) */}
      {teammates.length > 0 ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            {t("roleComparison.teammatesTitle", { min: MIN_MATCHES })}
          </p>
          <div className="space-y-1.5">
            {teammates.map((tm) => (
              <TeammateRow
                key={tm.stat.player_id}
                stat={tm.stat}
                score={tm.score}
                myScore={myScore}
                t={t}
                onOpenPlayer={onOpenPlayer}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">
          {t("roleComparison.noTeammates", { min: MIN_MATCHES })}
        </p>
      )}

      {/* Selettore + confronto 1vs1 dettagliato (radar + KPI side-by-side) */}
      {teammates.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 sm:p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Swords className="h-4 w-4 text-primary shrink-0" />
            <p className="text-[11px] uppercase tracking-wider font-semibold text-primary">
              {t("roleComparison.versus.title")}
            </p>
            <p className="text-xs text-muted-foreground ml-auto">
              {t("roleComparison.versus.pickHint")}
            </p>
          </div>
          <Select
            value={versusId ?? ""}
            onValueChange={(v) => setVersusId(v || null)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("roleComparison.versus.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {/* Ordine alfabetico nel dropdown: serve a trovare un nome
                  preciso, non a vedere la classifica (quella sta in chiaro
                  nella lista compagni qui sopra). Lo score resta visibile
                  accanto al nome di ogni voce. */}
              {[...teammates]
                .sort((a, b) => a.stat.display_name.localeCompare(b.stat.display_name))
                .map((tm) => (
                  <SelectItem key={tm.stat.player_id} value={tm.stat.player_id}>
                    {tm.stat.display_name} · {tm.score}/100 · {tm.stat.matches} {t("compare.matchesUnit")}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {versus && (
            <DetailedOneVsOne
              position={position}
              me={me}
              myScore={myScore}
              opponent={versus.stat}
              opponentScore={versus.score}
              sources={sources}
              labels={labels}
              benchmark={benchmark}
              perMatchBenchmark={perMatchBenchmark}
              onShare={openShareVs}
              t={t}
            />
          )}
        </div>
      )}

      {/* Bottone "Condividi confronto": apre un dialog con la preview e
          tre azioni (scarica, copia, condividi). Vedi ShareDialog. */}
      <div className="flex justify-end pt-2">
        <Button type="button" variant="outline" size="sm" onClick={openShareSingle}>
          <Share2 className="h-3.5 w-3.5 mr-1.5" />
          {t("roleComparison.shareCta")}
        </Button>
      </div>

      <ShareDialog
        state={shareDialog}
        onClose={() => setShareDialog(null)}
        t={t}
      />

      {/* Preview "shareable" off-screen: stessa info ma con header brand
          completo + footer firma. Catturata da html-to-image al click. */}
      <ShareablePreview
        containerRef={shareRef}
        playerName={me.display_name}
        positionLabel={t(`compare.position.${position}`)}
        matches={me.matches}
        myScore={myScore}
        sources={sources}
        labels={labels}
        stat={me}
        benchmark={benchmark}
        perMatchBenchmark={perMatchBenchmark}
        teammates={teammates}
        t={t}
      />

      {versus && (
        <ShareableOneVsOne
          containerRef={shareVsRef}
          positionLabel={t(`compare.position.${position}`)}
          me={me}
          myScore={myScore}
          opponent={versus.stat}
          opponentScore={versus.score}
          sources={sources}
          labels={labels}
          benchmark={benchmark}
          perMatchBenchmark={perMatchBenchmark}
          t={t}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Preview "share": versione brandizzata della card, montata fuori dalla
// viewport (off-screen). Quando l'utente preme "Condividi" la catturiamo
// con html-to-image. Tiene il BrandMark completo in alto come richiesto.
// ──────────────────────────────────────────────────────────────────────────
const ShareablePreview = ({
  containerRef,
  playerName,
  positionLabel,
  matches,
  myScore,
  sources,
  labels,
  stat,
  benchmark,
  perMatchBenchmark,
  teammates,
  t,
}: {
  // React 18 tratta `ref` come prop riservata: passandola con quel nome non
  // arriva al figlio (serve forwardRef). Per evitare il boilerplate uso
  // `containerRef` come prop ordinaria e la attacco manualmente al div.
  containerRef: React.RefObject<HTMLDivElement | null>;
  playerName: string;
  positionLabel: string;
  matches: number;
  myScore: number;
  sources: SlotSource[];
  labels: [string, string, string, string, string, string];
  stat: PositionStats;
  benchmark: Benchmark | null;
  perMatchBenchmark: { clean_sheets_per_match: number; saves_per_match: number } | null;
  teammates: Array<{ stat: PositionStats; score: number }>;
  t: (k: string, opts?: any) => string;
}) => {
  // Off-screen sopra la viewport (top: -10000px). Diversamente da
  // `left: -99999px` o `width:0 + overflow:hidden`, qui il browser è
  // costretto a layoutare il container con la sua larghezza reale
  // (720px) e tutti i suoi figli: html-to-image trova un bounding box
  // valido e produce un PNG completo. `pointer-events: none` + `z-index: -1`
  // garantiscono che non interferisca con la UI principale.
  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: "fixed",
        top: "-10000px",
        left: 0,
        width: "720px",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          background: "#0a1114",
          color: "#f1f5f4",
          padding: "32px",
          borderRadius: "24px",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Header brand */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          {/* Logo inline: niente dipendenza dalla classe .dark del body
              (il CSS .eb-brand-logo viene perso dal clone di html-to-image),
              applico filter esplicito qui per renderlo bianco sullo sfondo
              scuro del preview. crossOrigin per assicurare la cattura. */}
          <img
            src={ELEVENBASE_LOGO_URL}
            alt="ElevenBase"
            style={{
              height: "48px",
              width: "auto",
              filter: "brightness(0) invert(1)",
              display: "block",
            }}
          />
          <div
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "rgba(241,245,244,0.55)",
              fontWeight: 600,
            }}
          >
            {t("roleComparison.shareWatermark")}
          </div>
        </div>

        {/* Identità player + score */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "rgba(241,245,244,0.55)",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              {positionLabel} · {t("roleComparison.matchesInRole", { count: matches })}
            </div>
            <div style={{ fontSize: "28px", fontWeight: 700, lineHeight: 1.1 }}>
              {playerName}
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              padding: "12px 18px",
              borderRadius: "16px",
              background: "rgba(99,102,241,0.15)",
              border: "1px solid rgba(99,102,241,0.4)",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "rgba(165,180,252,1)",
                fontWeight: 600,
              }}
            >
              {t("roleComparison.myScore")}
            </div>
            <div
              style={{
                fontSize: "36px",
                fontWeight: 800,
                color: "rgba(165,180,252,1)",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {myScore}
              <span style={{ fontSize: "14px", color: "rgba(241,245,244,0.55)", fontWeight: 500 }}>
                /100
              </span>
            </div>
          </div>
        </div>

        {/* Griglia KPI vs team */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          {sources.map((src, i) => {
            const my = rawValueOf(stat, src, benchmark);
            const teamVal =
              src.kind === "stat" && src.benchmarkKey
                ? benchmark?.[src.benchmarkKey] ?? null
                : src.kind === "perMatch"
                  ? perMatchBenchmark?.[src.numerator === "clean_sheets" ? "clean_sheets_per_match" : "saves_per_match"] ?? null
                  : null;
            const d = teamVal != null ? deltaPct(my, teamVal) : null;
            const tone =
              d?.status === "above" ? "#c8f25a" : d?.status === "below" ? "#ff5d52" : "rgba(241,245,244,0.55)";
            return (
              <div
                key={i}
                style={{
                  background: "rgba(15,23,42,0.6)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "12px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: "rgba(241,245,244,0.55)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: "4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t(labels[i])}
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.1,
                  }}
                >
                  {formatRawValue(metricKeyFromSource(src), my)}
                </div>
                {d && (
                  <div style={{ fontSize: "11px", color: tone, fontVariantNumeric: "tabular-nums" }}>
                    {d.status === "inline"
                      ? t("compare.deltas.inline")
                      : `${d.pct > 0 ? "+" : ""}${d.pct.toFixed(0)}%`}
                  </div>
                )}
                {teamVal != null && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "rgba(241,245,244,0.4)",
                      fontStyle: "italic",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    Ø {formatRawValue(metricKeyFromSource(src), teamVal)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Compagni (top 3) */}
        {teammates.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "rgba(241,245,244,0.55)",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              {t("roleComparison.teammatesTitle", { min: MIN_MATCHES })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {teammates.slice(0, 4).map((tm) => {
                const diff = tm.score - myScore;
                const tone = diff > 0 ? "#c8f25a" : diff < 0 ? "#ff5d52" : "rgba(241,245,244,0.55)";
                return (
                  <div
                    key={tm.stat.player_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      background: "rgba(15,23,42,0.5)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: "10px",
                      padding: "8px 12px",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {tm.stat.display_name}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "rgba(241,245,244,0.55)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {tm.stat.matches} {t("compare.matchesUnit")}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: "rgba(165,180,252,1)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {tm.score}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: tone,
                        fontVariantNumeric: "tabular-nums",
                        width: "36px",
                        textAlign: "right",
                      }}
                    >
                      {diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : "="}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            fontSize: "10px",
            color: "rgba(241,245,244,0.4)",
            textAlign: "center",
            paddingTop: "16px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            letterSpacing: "0.05em",
          }}
        >
          {t("roleComparison.shareFooter")}
        </div>
      </div>
    </div>
  );
};

// Converte un data URL "data:image/png;base64,..." in Blob senza usare
// fetch(): la CSP del sito non include `data:` né in connect-src né in
// img-src, e fetch su un data URL viene bloccato. Implementazione
// "atob → Uint8Array → Blob" che non richiede fetch.
function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, base64] = dataUrl.split(",");
  const mimeMatch = meta.match(/:(.*?);/);
  const mime = mimeMatch?.[1] || "image/png";
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Aspetta che tutte le <img> dentro il container completino il decode.
// html-to-image fa già internamente il fetch delle immagini, ma se il browser
// sta ancora caricando il logo al momento dello scatto la prima cattura può
// produrre un PNG col logo bianco. Una sola Promise.all sui decode è
// sufficiente, di solito risolve in 0-50ms.
async function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  if (imgs.length === 0) return;
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      // decode() risolve quando i pixel sono pronti per essere disegnati su
      // canvas. Su browser che non lo implementano fallback su load event.
      if ("decode" in img) {
        return img.decode().catch(() => undefined);
      }
      return new Promise<void>((resolve) => {
        (img as HTMLImageElement).addEventListener("load", () => resolve(), { once: true });
        (img as HTMLImageElement).addEventListener("error", () => resolve(), { once: true });
      });
    }),
  );
}

function deltaPct(my: number, team: number): { pct: number; status: "above" | "below" | "inline" } {
  const denom = Math.max(0.0001, Math.abs(team));
  const pct = ((my - team) / denom) * 100;
  const status: "above" | "below" | "inline" = Math.abs(pct) < 5 ? "inline" : pct > 0 ? "above" : "below";
  return { pct, status };
}

function KpiTile({
  label,
  value,
  teamValue,
  delta,
  t,
}: {
  label: string;
  value: string;
  teamValue: string | null;
  delta: { pct: number; status: "above" | "below" | "inline" } | null;
  t: (k: string, opts?: any) => string;
}) {
  const tone =
    delta?.status === "above"
      ? "text-emerald-600 dark:text-emerald-400"
      : delta?.status === "below"
        ? "text-rose-600 dark:text-rose-400"
        : "text-muted-foreground";
  const Icon = delta?.status === "above" ? TrendingUp : delta?.status === "below" ? TrendingDown : null;
  return (
    <div className="rounded-lg border bg-background/40 px-2.5 py-2 min-w-0 overflow-hidden">
      <p className="text-[10px] text-muted-foreground truncate">{label}</p>
      <p className="text-sm font-semibold tabular-nums leading-tight">{value}</p>
      {delta != null && (
        <p className={cn("text-[10px] inline-flex items-center gap-0.5 tabular-nums leading-tight", tone)}>
          {Icon && <Icon className="h-2.5 w-2.5" />}
          {delta.status === "inline"
            ? t("compare.deltas.inline")
            : `${delta.pct > 0 ? "+" : ""}${delta.pct.toFixed(0)}%`}
        </p>
      )}
      {teamValue != null && (
        <p className="text-[9px] text-muted-foreground italic tabular-nums leading-tight truncate">
          Ø {teamValue}
        </p>
      )}
    </div>
  );
}

function TeammateRow({
  stat,
  score,
  myScore,
  t,
  onOpenPlayer,
}: {
  stat: PositionStats;
  score: number;
  myScore: number;
  t: (k: string, opts?: any) => string;
  onOpenPlayer?: (id: string) => void;
}) {
  const reliability = reliabilityOf(stat.matches);
  const reliabilityTone =
    reliability === "high"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : reliability === "medium"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
        : "bg-rose-500/15 text-rose-700 dark:text-rose-400";
  const diff = score - myScore;
  const ahead = score > myScore;
  const initials = stat.display_name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const Wrapper: React.ElementType = onOpenPlayer ? "button" : "div";
  return (
    <Wrapper
      type={onOpenPlayer ? "button" : undefined}
      onClick={onOpenPlayer ? () => onOpenPlayer(stat.player_id) : undefined}
      className={cn(
        "w-full flex items-center gap-2 rounded-lg border bg-background/40 px-3 py-2 text-left transition-colors",
        onOpenPlayer && "hover:bg-muted/40",
      )}
    >
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-[10px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate flex items-center gap-1.5">
          {ahead && <Crown className="h-3 w-3 text-primary shrink-0" />}
          <span className="truncate">{stat.display_name}</span>
          {stat.is_guest && (
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground shrink-0">
              {t("compare.guestTag")}
            </span>
          )}
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">
          {stat.matches} {t("compare.matchesUnit")}
        </p>
      </div>
      <Badge
        variant="outline"
        className={cn(
          "rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider border-0",
          reliabilityTone,
        )}
      >
        {t(`compare.sample.${reliability}`)}
      </Badge>
      <div className="text-right shrink-0">
        <p className="text-base font-bold tabular-nums text-primary leading-tight">{score}</p>
        <p
          className={cn(
            "text-[10px] tabular-nums leading-tight",
            diff > 0 ? "text-emerald-600 dark:text-emerald-400" : diff < 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground",
          )}
        >
          {diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : "="}
        </p>
      </div>
      {onOpenPlayer && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
    </Wrapper>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// DetailedOneVsOne: confronto 1vs1 "dettagliato come quello del founder":
// radar overlay con i 2 player + grid completa dei KPI side-by-side +
// badge metriche vinte. La preview da condividere riusa gli stessi dati.
// ──────────────────────────────────────────────────────────────────────────

function DetailedOneVsOne({
  position,
  me,
  myScore,
  opponent,
  opponentScore,
  sources,
  labels,
  benchmark,
  perMatchBenchmark,
  onShare,
  t,
}: {
  position: EaPosition;
  me: PositionStats;
  myScore: number;
  opponent: PositionStats;
  opponentScore: number;
  sources: SlotSource[];
  labels: [string, string, string, string, string, string];
  benchmark: Benchmark | null;
  perMatchBenchmark: { clean_sheets_per_match: number; saves_per_match: number } | null;
  onShare: () => void;
  t: (k: string, opts?: any) => string;
}) {
  // Dati radar normalizzati 0-100 per entrambi i player, stessa logica del
  // Confronto (radarValuesFor). Overlay diretto: il visitatore vede al volo
  // il "forma" delle due silhouette.
  const radarDatasets: RadarDataset[] = [
    {
      label: me.display_name,
      values: radarValuesFor(me, position, benchmark),
      color: COLORS[0],
    },
    {
      label: opponent.display_name,
      values: radarValuesFor(opponent, position, benchmark),
      color: COLORS[1],
    },
  ];

  // Calcolo vincitore per ciascuna delle 6 metriche (valori RAW dei KPI,
  // gli stessi mostrati nella griglia sotto). Banner "X/6 metriche vinte"
  // per ognuno.
  const wins = sources.reduce(
    (acc, src) => {
      const my = rawValueOf(me, src, benchmark);
      const his = rawValueOf(opponent, src, benchmark);
      if (my > his + 0.001) acc.me += 1;
      else if (his > my + 0.001) acc.opp += 1;
      return acc;
    },
    { me: 0, opp: 0 },
  );

  return (
    <div className="space-y-3">
      {/* Header con i due nomi + score + match */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="text-right min-w-0">
          <p className="text-sm font-semibold truncate flex items-center justify-end gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ background: COLORS[0] }}
              aria-hidden
            />
            <span className="truncate">{me.display_name}</span>
          </p>
          <p className="text-2xl font-bold tabular-nums text-primary leading-tight">
            {myScore}
            <span className="text-xs text-muted-foreground font-medium">/100</span>
          </p>
          <p className="text-[10px] text-muted-foreground tabular-nums">
            {me.matches} {t("compare.matchesUnit")} · {wins.me}/{sources.length} {t("roleComparison.versus.metricsWonShort")}
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-1">vs</div>
        <div className="text-left min-w-0">
          <p className="text-sm font-semibold truncate flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ background: COLORS[1] }}
              aria-hidden
            />
            <span className="truncate">{opponent.display_name}</span>
          </p>
          <p className="text-2xl font-bold tabular-nums text-primary leading-tight">
            {opponentScore}
            <span className="text-xs text-muted-foreground font-medium">/100</span>
          </p>
          <p className="text-[10px] text-muted-foreground tabular-nums">
            {opponent.matches} {t("compare.matchesUnit")} · {wins.opp}/{sources.length} {t("roleComparison.versus.metricsWonShort")}
          </p>
        </div>
      </div>

      {/* Radar overlay */}
      <div className="w-full max-w-[320px] mx-auto">
        <RadarChart
          axes={labels.map((l) => t(l))}
          datasets={radarDatasets}
          size={280}
        />
      </div>

      {/* KPI radar (raffinati): i 6 valori sintetici usati per lo score */}
      <div className="space-y-1.5">
        {sources.map((src, i) => {
          const my = rawValueOf(me, src, benchmark);
          const his = rawValueOf(opponent, src, benchmark);
          const myWins = my > his + 0.001;
          const hisWins = his > my + 0.001;
          const key = metricKeyFromSource(src);
          return (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <p
                className={cn(
                  "text-sm text-right tabular-nums",
                  myWins ? "font-bold text-primary" : "text-foreground/80",
                )}
              >
                {myWins && <Crown className="inline h-3 w-3 mr-0.5 text-primary" />}
                {formatRawValue(key, my)}
              </p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 truncate text-center">
                {t(labels[i])}
              </p>
              <p
                className={cn(
                  "text-sm text-left tabular-nums",
                  hisWins ? "font-bold text-primary" : "text-foreground/80",
                )}
              >
                {hisWins && <Crown className="inline h-3 w-3 mr-0.5 text-primary" />}
                {formatRawValue(key, his)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Statistiche complete (assolute + per match): tutto quello che non
          è già nei 6 KPI del radar. */}
      <FullStatsTable
        me={me}
        opponent={opponent}
        position={position}
        benchmark={benchmark}
        t={t}
      />

      {/* Share 1vs1 → apre il ShareDialog brandizzato */}
      <div className="flex justify-end pt-1">
        <Button type="button" variant="default" size="sm" onClick={onShare}>
          <Share2 className="h-3.5 w-3.5 mr-1.5" />
          {t("roleComparison.versus.shareCta")}
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// FullStatsTable: tutte le stat del ruolo affiancate per i due player.
// Mostra metriche assolute (matches, goals totali, ecc.) + percentuali
// raffinate (rating, win_rate, pass_pct, ecc.) + per-match. Sezionata in
// gruppi tematici per leggibilità.
// ──────────────────────────────────────────────────────────────────────────

type StatRow = {
  key: keyof PositionStats;
  labelKey: string;
  fmt: (v: number) => string;
  lowerIsBetter?: boolean;
};
type StatGroup = { headerKey: string; rows: StatRow[] };

function roleStatGroups(position: EaPosition): StatGroup[] {
  const isGk = position === "goalkeeper";
  const generale: StatRow[] = [
    { key: "matches",       labelKey: "compare.metric.matches",     fmt: (v) => String(v) },
    { key: "minutes_avg",   labelKey: "compare.metric.minutesAvg",  fmt: (v) => `${(v ?? 0).toFixed(0)}'` },
    { key: "avg_rating",    labelKey: "compare.metric.avgRating",   fmt: (v) => v.toFixed(2) },
    { key: "win_rate",      labelKey: "compare.metric.winRate",     fmt: (v) => `${v.toFixed(1)}%` },
    { key: "wins",          labelKey: "compare.metric.wins",        fmt: (v) => String(v) },
    { key: "losses",        labelKey: "compare.metric.losses",      fmt: (v) => String(v), lowerIsBetter: true },
    { key: "draws",         labelKey: "compare.metric.draws",       fmt: (v) => String(v) },
    { key: "mom",           labelKey: "compare.metric.mom",         fmt: (v) => String(v) },
    { key: "mom_per_match", labelKey: "compare.metric.momPct",      fmt: (v) => `${v.toFixed(1)}%` },
    { key: "red_cards",     labelKey: "compare.metric.redCards",    fmt: (v) => String(v), lowerIsBetter: true },
  ];
  const offensive: StatRow[] = [
    { key: "goals",             labelKey: "compare.metric.goals",            fmt: (v) => String(v) },
    { key: "goals_per_match",   labelKey: "compare.metric.goalsPerMatch",    fmt: (v) => v.toFixed(2) },
    { key: "assists",           labelKey: "compare.metric.assists",          fmt: (v) => String(v) },
    { key: "assists_per_match", labelKey: "compare.metric.assistsPerMatch",  fmt: (v) => v.toFixed(2) },
    { key: "shots",             labelKey: "compare.metric.shots",            fmt: (v) => String(v) },
  ];
  const tecnical: StatRow[] = [
    { key: "passes_made",     labelKey: "compare.metric.passesMade",     fmt: (v) => String(v) },
    { key: "pass_attempts",   labelKey: "compare.metric.passAttempts",   fmt: (v) => String(v) },
    { key: "pass_pct",        labelKey: "compare.metric.passPct",        fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
    { key: "tackles_made",    labelKey: "compare.metric.tacklesMade",    fmt: (v) => String(v) },
    { key: "tackle_attempts", labelKey: "compare.metric.tackleAttempts", fmt: (v) => String(v) },
    { key: "tackle_pct",      labelKey: "compare.metric.tacklePct",      fmt: (v) => `${(v ?? 0).toFixed(1)}%` },
  ];
  const gk: StatRow[] = [
    { key: "clean_sheets",   labelKey: "compare.metric.cleanSheets",   fmt: (v) => String(v) },
    { key: "saves",          labelKey: "compare.metric.saves",         fmt: (v) => String(v) },
    { key: "goals_conceded", labelKey: "compare.metric.goalsConceded", fmt: (v) => String(v), lowerIsBetter: true },
  ];
  if (isGk) {
    return [
      { headerKey: "roleComparison.versus.fullStatsGroups.general", rows: generale },
      { headerKey: "roleComparison.versus.fullStatsGroups.keeper",  rows: gk },
      { headerKey: "roleComparison.versus.fullStatsGroups.tecnical", rows: tecnical },
    ];
  }
  return [
    { headerKey: "roleComparison.versus.fullStatsGroups.general",   rows: generale },
    { headerKey: "roleComparison.versus.fullStatsGroups.offensive", rows: offensive },
    { headerKey: "roleComparison.versus.fullStatsGroups.tecnical",  rows: tecnical },
  ];
}

function FullStatsTable({
  me,
  opponent,
  position,
  benchmark,
  t,
}: {
  me: PositionStats;
  opponent: PositionStats;
  position: EaPosition;
  benchmark: Benchmark | null;
  t: (k: string, opts?: any) => string;
}) {
  const groups = roleStatGroups(position);
  return (
    <div className="space-y-3 pt-2 border-t border-border/50">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {t("roleComparison.versus.fullStatsTitle")}
      </p>
      {groups.map((g) => (
        <div key={g.headerKey} className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
            {t(g.headerKey)}
          </p>
          <div className="space-y-1">
            {g.rows.map((row) => {
              // Valori "raffinati" per i KPI nel benchmark; raw per tutto il resto
              // (matches, goals, assists, shots, ecc.) che sono contatori.
              const myVal = statValueOf(me, row.key, benchmark);
              const hisVal = statValueOf(opponent, row.key, benchmark);
              const eps = 0.001;
              const myWins = row.lowerIsBetter
                ? myVal < hisVal - eps
                : myVal > hisVal + eps;
              const hisWins = row.lowerIsBetter
                ? hisVal < myVal - eps
                : hisVal > myVal + eps;
              return (
                <div key={String(row.key)} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                  <p
                    className={cn(
                      "text-[12.5px] text-right tabular-nums",
                      myWins ? "font-bold text-primary" : "text-foreground/70",
                    )}
                  >
                    {myWins && <Crown className="inline h-3 w-3 mr-0.5 text-primary" />}
                    {row.fmt(myVal)}
                  </p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 truncate text-center">
                    {t(row.labelKey)}
                  </p>
                  <p
                    className={cn(
                      "text-[12.5px] text-left tabular-nums",
                      hisWins ? "font-bold text-primary" : "text-foreground/70",
                    )}
                  >
                    {hisWins && <Crown className="inline h-3 w-3 mr-0.5 text-primary" />}
                    {row.fmt(hisVal)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ShareableOneVsOne: preview off-screen brandizzata per il 1vs1.
// Mantiene lo stesso stile dello share singolo, con due colonne.
// ──────────────────────────────────────────────────────────────────────────
const ShareableOneVsOne = ({
  containerRef,
  positionLabel,
  me,
  myScore,
  opponent,
  opponentScore,
  sources,
  labels,
  benchmark,
  perMatchBenchmark,
  t,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  positionLabel: string;
  me: PositionStats;
  myScore: number;
  opponent: PositionStats;
  opponentScore: number;
  sources: SlotSource[];
  labels: [string, string, string, string, string, string];
  benchmark: Benchmark | null;
  perMatchBenchmark: { clean_sheets_per_match: number; saves_per_match: number } | null;
  t: (k: string, opts?: any) => string;
}) => {
  const myWins = sources.reduce(
    (n, src) => (rawValueOf(me, src, benchmark) > rawValueOf(opponent, src, benchmark) + 0.001 ? n + 1 : n),
    0,
  );
  const oppWins = sources.reduce(
    (n, src) => (rawValueOf(opponent, src, benchmark) > rawValueOf(me, src, benchmark) + 0.001 ? n + 1 : n),
    0,
  );
  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: "fixed",
        left: "-99999px",
        top: 0,
        width: "720px",
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          background: "#0a1114",
          color: "#f1f5f4",
          padding: "32px",
          borderRadius: "24px",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Header brand */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          {/* Logo inline: niente dipendenza dalla classe .dark del body
              (il CSS .eb-brand-logo viene perso dal clone di html-to-image),
              applico filter esplicito qui per renderlo bianco sullo sfondo
              scuro del preview. crossOrigin per assicurare la cattura. */}
          <img
            src={ELEVENBASE_LOGO_URL}
            alt="ElevenBase"
            style={{
              height: "48px",
              width: "auto",
              filter: "brightness(0) invert(1)",
              display: "block",
            }}
          />
          <div
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "rgba(241,245,244,0.55)",
              fontWeight: 600,
            }}
          >
            {t("roleComparison.versus.shareWatermark", { position: positionLabel })}
          </div>
        </div>

        {/* Header 1vs1 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div style={{ textAlign: "right", minWidth: 0 }}>
            <div style={{ fontSize: "20px", fontWeight: 700, lineHeight: 1.1 }}>
              {me.display_name}
            </div>
            <div
              style={{
                fontSize: "44px",
                fontWeight: 800,
                color: "rgba(165,180,252,1)",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {myScore}
              <span
                style={{ fontSize: "16px", color: "rgba(241,245,244,0.55)", fontWeight: 500 }}
              >
                /100
              </span>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "rgba(241,245,244,0.55)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {me.matches} {t("compare.matchesUnit")} · {myWins} {t("roleComparison.versus.metricsWonShort")}
            </div>
          </div>
          <div
            style={{
              padding: "8px 14px",
              borderRadius: "999px",
              background: "rgba(99,102,241,0.2)",
              border: "1px solid rgba(99,102,241,0.4)",
              fontSize: "14px",
              fontWeight: 800,
              color: "rgba(165,180,252,1)",
              letterSpacing: "0.1em",
            }}
          >
            VS
          </div>
          <div style={{ textAlign: "left", minWidth: 0 }}>
            <div style={{ fontSize: "20px", fontWeight: 700, lineHeight: 1.1 }}>
              {opponent.display_name}
            </div>
            <div
              style={{
                fontSize: "44px",
                fontWeight: 800,
                color: "rgba(165,180,252,1)",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {opponentScore}
              <span
                style={{ fontSize: "16px", color: "rgba(241,245,244,0.55)", fontWeight: 500 }}
              >
                /100
              </span>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "rgba(241,245,244,0.55)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {opponent.matches} {t("compare.matchesUnit")} · {oppWins} {t("roleComparison.versus.metricsWonShort")}
            </div>
          </div>
        </div>

        {/* Ruolo banner */}
        <div
          style={{
            fontSize: "10px",
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: "rgba(241,245,244,0.55)",
            fontWeight: 600,
            marginBottom: "8px",
            textAlign: "center",
          }}
        >
          {positionLabel}
        </div>

        {/* Griglia KPI side-by-side */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "20px" }}>
          {sources.map((src, i) => {
            const my = rawValueOf(me, src, benchmark);
            const his = rawValueOf(opponent, src, benchmark);
            const myWinsThis = my > his + 0.001;
            const hisWinsThis = his > my + 0.001;
            const key = metricKeyFromSource(src);
            return (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  gap: "12px",
                  background: "rgba(15,23,42,0.5)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: "10px",
                  padding: "10px 14px",
                }}
              >
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: myWinsThis ? 800 : 500,
                    color: myWinsThis ? "rgba(165,180,252,1)" : "rgba(241,245,244,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "right",
                  }}
                >
                  {formatRawValue(key, my)}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "rgba(241,245,244,0.55)",
                    textAlign: "center",
                    minWidth: "90px",
                  }}
                >
                  {t(labels[i])}
                </div>
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: hisWinsThis ? 800 : 500,
                    color: hisWinsThis ? "rgba(165,180,252,1)" : "rgba(241,245,244,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "left",
                  }}
                >
                  {formatRawValue(key, his)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            fontSize: "10px",
            color: "rgba(241,245,244,0.4)",
            textAlign: "center",
            paddingTop: "16px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            letterSpacing: "0.05em",
          }}
        >
          {t("roleComparison.shareFooter")}
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// ShareDialog: dialog modale che mostra la preview generata e offre tre
// azioni (scarica, copia, condividi). Più robusto del flusso "click → share
// nativo" che falliva silenziosamente quando navigator.share non era
// supportato (Chrome desktop / qualunque browser non-mobile).
// ──────────────────────────────────────────────────────────────────────────

type ShareDialogState = {
  kind: "single" | "versus";
  dataUrl: string | null;
  error: string | null;
  fileName: string;
  shareTitle: string;
  shareText: string;
};

function ShareDialog({
  state,
  onClose,
  t,
}: {
  state: ShareDialogState | null;
  onClose: () => void;
  t: (k: string, opts?: any) => string;
}) {
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reset transient state al cambio di dialog (apertura/chiusura/swap kind).
  useEffect(() => {
    setCopied(false);
    setActionError(null);
  }, [state?.kind, state?.dataUrl]);

  const handleDownload = () => {
    if (!state?.dataUrl) return;
    try {
      const link = document.createElement("a");
      link.href = state.dataUrl;
      link.download = state.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "download_failed");
    }
  };

  const handleCopy = async () => {
    if (!state?.dataUrl) return;
    try {
      const blob = dataUrlToBlob(state.dataUrl);
      const nav: any = navigator;
      if (!nav?.clipboard?.write || typeof ClipboardItem === "undefined") {
        throw new Error("clipboard_unsupported");
      }
      await nav.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setActionError(null);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("[ShareDialog] copy failed", err);
      setActionError(t("roleComparison.shareDialog.copyUnsupported"));
    }
  };

  const handleNativeShare = async () => {
    if (!state?.dataUrl) return;
    try {
      const blob = dataUrlToBlob(state.dataUrl);
      const file = new File([blob], state.fileName, { type: "image/png" });
      const nav: any = navigator;
      if (nav?.canShare?.({ files: [file] })) {
        await nav.share({
          files: [file],
          title: state.shareTitle,
          text: state.shareText,
        });
      } else if (nav?.share) {
        await nav.share({
          title: state.shareTitle,
          text: state.shareText,
          url: window.location.href,
        });
      } else {
        setActionError(t("roleComparison.shareDialog.shareUnsupported"));
      }
    } catch (err) {
      if (err instanceof Error && err.name !== "AbortError") {
        console.error("[ShareDialog] native share failed", err);
        setActionError(err.message);
      }
    }
  };

  const canShareNative =
    typeof navigator !== "undefined" && typeof (navigator as any).share === "function";

  return (
    <Dialog open={!!state} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("roleComparison.shareDialog.title")}</DialogTitle>
          <DialogDescription>{t("roleComparison.shareDialog.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* Preview */}
          <div className="rounded-xl border bg-muted/30 p-2 min-h-[160px] flex items-center justify-center">
            {!state ? null : state.error ? (
              <div className="text-center space-y-1.5 py-6">
                <X className="h-6 w-6 text-rose-500 mx-auto" />
                <p className="text-sm text-rose-600 dark:text-rose-400 font-medium">
                  {t("roleComparison.shareDialog.error")}
                </p>
                <p className="text-[11px] text-muted-foreground font-mono">{state.error}</p>
              </div>
            ) : state.dataUrl ? (
              <img
                src={state.dataUrl}
                alt=""
                className="max-w-full h-auto rounded-md shadow-md"
              />
            ) : (
              <div className="text-center space-y-2 py-6">
                <Loader2 className="h-6 w-6 text-muted-foreground mx-auto animate-spin" />
                <p className="text-sm text-muted-foreground">
                  {t("roleComparison.shareDialog.generating")}
                </p>
              </div>
            )}
          </div>

          {actionError && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              {actionError}
            </div>
          )}

          {/* Azioni */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button
              type="button"
              variant="default"
              onClick={handleDownload}
              disabled={!state?.dataUrl}
            >
              <Download className="h-4 w-4 mr-1.5" />
              {t("roleComparison.shareDialog.download")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCopy}
              disabled={!state?.dataUrl}
            >
              {copied ? (
                <Check className="h-4 w-4 mr-1.5 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4 mr-1.5" />
              )}
              {copied
                ? t("roleComparison.shareDialog.copied")
                : t("roleComparison.shareDialog.copy")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleNativeShare}
              disabled={!state?.dataUrl || !canShareNative}
              title={
                canShareNative
                  ? undefined
                  : t("roleComparison.shareDialog.shareUnsupported")
              }
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              {t("roleComparison.shareDialog.share")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
